import {
  DOCK_STATE,
  SIDEKICK_DOCK_BAR_HEIGHT_PX,
  SIDEKICK_DOCK_EDGE_OFFSET_PX,
  SIDEKICK_DOCK_EXPANDED_HEIGHT_PX,
  SIDEKICK_DOCK_WIDTH_PX,
  SIDEKICK_PANEL_WIDTH,
  selectDockState,
  selectIsPanelOpen,
  useIsSidekickEnabled,
  useIsSidekickM1Enabled,
} from '@brightdrop/ai-sidekick';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useSelector } from 'react-redux';

import { GLOBAL_SNACKBAR_STACK_GAP_PX } from '~/components/GlobalSnackbarStack/constants';

import {
  SIDEKICK_DOCK_STACK_BESIDE_DRAWER_MIN_WIDTH_PX,
  SIDEKICK_DOCK_STACK_BESIDE_MIN_WIDTH_PX,
} from './constants';
import { useIsSidekickDockRoute } from './useIsSidekickDockRoute';

/**
 * The corner the docked assistant is holding, in the two directions a
 * neighbour can move to get out of its way.
 */
export type SidekickDockInset = {
  /** Height (px) taken at the viewport's bottom edge. */
  bottomPx: number;
  /** Width (px) taken at the viewport's right edge. */
  rightPx: number;
  /**
   * True when the assistant leaves no usable corner at all, so a
   * neighbour has to stand down rather than move. Both axes read 0
   * here — there is no direction left to offer.
   */
  leavesNoRoom: boolean;
};

const NO_INSET: SidekickDockInset = {
  bottomPx: 0,
  rightPx: 0,
  leavesNoRoom: false,
};

const NO_ROOM: SidekickDockInset = {
  bottomPx: 0,
  rightPx: 0,
  leavesNoRoom: true,
};

/**
 * How much of the bottom-right corner the docked assistant is
 * occupying, so anything else anchored there can move clear of it
 * instead of drawing behind it.
 *
 * The dock and its drawer are fixed-position and mounted app-wide, so
 * nothing in the layout pushes them around; a neighbour has to be
 * told how much room they have taken. This is the one place that
 * answers that, and it mirrors every condition that decides what the
 * assistant draws: the route, both flags, the drawer it yields to,
 * and which resting size the dock is in.
 *
 * The answer is not always vertical. The drawer replaces the dock
 * along the whole right edge, and a viewport too short to hold the
 * neighbour above the expanded card cannot lift it at all, so both of
 * those move it sideways instead.
 *
 * @param neighbourHeightPx - Rendered height of the neighbour asking.
 * Whether it can be lifted above the expanded card depends on how
 * tall it currently is, and its widgets expand, collapse and swap to
 * taller terminal states on their own, so the caller measures itself
 * rather than being modelled here.
 * @returns The occupied corner, or zero on both axes when no
 * assistant surface is on screen.
 */
export const useSidekickDockInset = (
  neighbourHeightPx: number
): SidekickDockInset => {
  const isDockRoute = useIsSidekickDockRoute();
  const isSidekickEnabled = useIsSidekickEnabled();
  const isSidekickM1Enabled = useIsSidekickM1Enabled();
  const isPanelOpen = useSelector(selectIsPanelOpen);
  const dockState = useSelector(selectDockState);
  // Leaving an edge to move past is not the same as leaving room to
  // land in, so this asks for the whole footprint the two need rather
  // than for the breakpoint where `SidePanel` stops being full width.
  const canStackBesideDrawer = useMediaQuery(
    `(min-width:${SIDEKICK_DOCK_STACK_BESIDE_DRAWER_MIN_WIDTH_PX}px)`
  );
  // The card is drawn at its full height plus its gap from the
  // viewport bottom; above that the neighbour needs the gap it keeps
  // from the card and whatever it is currently drawn at.
  const canStackAboveCard = useMediaQuery(
    `(min-height:${
      SIDEKICK_DOCK_EXPANDED_HEIGHT_PX +
      SIDEKICK_DOCK_EDGE_OFFSET_PX +
      GLOBAL_SNACKBAR_STACK_GAP_PX +
      neighbourHeightPx
    }px)`
  );
  const canStackBesideCard = useMediaQuery(
    `(min-width:${SIDEKICK_DOCK_STACK_BESIDE_MIN_WIDTH_PX}px)`
  );

  if (!isDockRoute || !isSidekickEnabled || !isSidekickM1Enabled) {
    return NO_INSET;
  }

  // The drawer takes the right edge over from the dock and runs the
  // full height of the viewport, so there is nothing to clear
  // vertically — a neighbour has to clear its width. It also draws
  // below `zIndex.snackbar`, so staying put would put the neighbour
  // on top of the drawer's own composer and controls. On a row too
  // narrow to hold both there is no sideways left to offer either.
  if (isPanelOpen) {
    return canStackBesideDrawer
      ? { bottomPx: 0, rightPx: SIDEKICK_PANEL_WIDTH, leavesNoRoom: false }
      : NO_ROOM;
  }

  // The bar tucks into the viewport's bottom edge and is short enough
  // to clear at any height worth supporting.
  if (dockState !== DOCK_STATE.EXPANDED) {
    return {
      bottomPx: SIDEKICK_DOCK_BAR_HEIGHT_PX,
      rightPx: 0,
      leavesNoRoom: false,
    };
  }

  // The card floats clear of the bottom edge, so its offset counts as
  // occupied too.
  if (canStackAboveCard) {
    return {
      bottomPx: SIDEKICK_DOCK_EXPANDED_HEIGHT_PX + SIDEKICK_DOCK_EDGE_OFFSET_PX,
      rightPx: 0,
      leavesNoRoom: false,
    };
  }

  // Too short to stack: the card keeps the bottom edge and a
  // neighbour moves alongside it, provided the row is wide enough for
  // both. When it is not, the expanded card owns the corner outright
  // until it is minimized again.
  return canStackBesideCard
    ? {
        bottomPx: 0,
        rightPx: SIDEKICK_DOCK_WIDTH_PX + SIDEKICK_DOCK_EDGE_OFFSET_PX,
        leavesNoRoom: false,
      }
    : NO_ROOM;
};
